import { EntryRepository, ParkedRepository, ResourceRepository } from '@sortera/dvfv-persistence'
import { unwrapArray } from '@sortera/ee-utils'
import { defineComponent } from 'vue'
// eslint-disable-next-line no-restricted-imports
import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router'

import { REGIONS, regionToCompanies, stringToRegionCode } from '@/domain/regions.ts'
import { t } from '@/services/translation/index.ts'
import { todayIso } from '@/services/use-current-date.ts'
import { useUser } from '@/services/use-user.ts'
import type { ViewErrorProps } from '@/views/error/ViewError.vue'
import ViewLayout from '@/views/layout/ViewLayout.vue'

import {
  MASKED_ROUTE_PATH,
  ROUTE_NAMES,
  ROUTER_ERROR_MESSAGE_403,
  ROUTER_ERROR_MESSAGE_404,
  ROUTER_ERROR_MESSAGE_MISSES_DRIVER_CONFIG,
} from './constants.ts'

const entryRepository = new EntryRepository()

export const routes: RouteRecordRaw[] = [
  // NO LAYOUT

  // These auth helper pages will never be reached via the router.
  // Adding these only here to indicate that the URLs are taken.
  // We are rewriting to the statically hosted .html files,
  // that can be found under the same path in the public folder.
  // Locally, the dev server is taking care of the rewrite.
  // When deploying to a web server, a reverse proxy (independent or built in web server)
  // should handle directing incoming traffic to the proper files if they match these path
  { path: '/__/auth/handler', name: ROUTE_NAMES.AUTH_HANDLER, redirect: '' },
  { path: '/__/auth/iframe', name: ROUTE_NAMES.AUTH_IFRAME, redirect: '' },

  {
    // If 3rd party cookies are blocked, we can't use the idToken from the firebase auth flow.
    // The user need to login one more time, directly with MSAL.
    // Therefore we have this separate redirect page.
    path: '/autentisering/logga-in',
    name: ROUTE_NAMES.AUTH_MSAL,
    component: () => import('@/views/auth/ViewAuthMsalRedirect.vue'),
    meta: { authRequired: false, title: () => t('auth.signing-in') },
  },

  // WRAPPED IN MAIN LAYOUT

  {
    path: '/',
    component: ViewLayout,
    // all children wrapped in layout
    children: [
      {
        path: '/',
        name: ROUTE_NAMES.HOME,
        // Should never be shown since we are redirecting. Vue router doesn't understand that though.
        // Ideally we would use `redirect` instead of `beforeResolve`,
        // but there you can not return a promise, which we must.
        component: defineComponent({
          template: '<div>Dummy, should never be shown since we are redirecting.</div>',
        }),
        meta: {
          authorizeNavigation({ user }) {
            if (user?.roles.admin || user?.roles.transportLeader) {
              return { name: ROUTE_NAMES.TRANSPORT_LEADER }
            }

            const userRegionCode = user?.driverConfig?.region
            const userResourceCode = user?.driverConfig?.resource
            if (userRegionCode && userResourceCode) {
              return {
                name: ROUTE_NAMES.ORDER_LIST,
                params: {
                  region: userRegionCode,
                  resource: userResourceCode,
                },
              }
            }
            return { name: ROUTE_NAMES.ERROR_MISSING_DRIVER_CONFIG, replace: true }
          },
          authRequired: true,
          title: () => t('routes.home'),
        },
      },
      {
        path: '/settings',
        name: ROUTE_NAMES.SETTINGS,
        meta: { authRequired: true, title: () => t('routes.settings') },
        component: () => import('@/views/settings/ViewSettings.vue'),
      },
      {
        path: '/roles-and-flags',
        name: ROUTE_NAMES.ROLES_AND_FLAGS,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.featureFlags?.developer) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: () => t('routes.roles-and-flags'),
        },
        component: () => import('@/views/roles-and-flags/ViewRolesAndFlags.vue'),
      },
      {
        // Redirect old deviations path to transport leader overview since the new deviations path is per-region
        path: '/transport-leaders/deviations',
        redirect: { name: ROUTE_NAMES.TRANSPORT_LEADER },
      },
      {
        path: '/dev',
        name: ROUTE_NAMES.TOOLS,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.featureFlags?.developer) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: () => t('routes.tools'),
        },
        component: () => import('@/views/dev/ViewDev.vue'),
      },
      {
        path: '/transport-leaders',
        name: ROUTE_NAMES.TRANSPORT_LEADER,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.roles.admin && !user?.roles.transportLeader) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: () => t('routes.transport-leaders'),
        },
        component: () => import('@/views/transport-leaders/ViewTransportLeaders.vue'),
      },
      {
        // In the past we used to call transport leaders "admins".
        // With the new user roles however, we separated the two.
        // Redirecting from the old URL, in case someone has it bookmarked.
        path: '/admin',
        redirect: { name: ROUTE_NAMES.TRANSPORT_LEADER },
      },
      {
        // Redirect old assign path to new path structure
        path: '/assign/:region',
        redirect: to => ({
          name: ROUTE_NAMES.ASSIGN,
          params: { region: to.params.region },
        }),
      },
      {
        path: '/region/:region',
        component: () => import('@/views/current-region-provider/ViewCurrentRegionProvider.vue'),
        meta: {
          authorizeNavigation({ toRoute }) {
            const regionParam = unwrapArray(toRoute.params.region)
            const isValidRegion = REGIONS.some(region => region.code === regionParam)
            if (!isValidRegion) {
              return new Error(ROUTER_ERROR_MESSAGE_404)
            }
            return undefined
          },
        },
        children: [
          {
            path: 'assign',
            name: ROUTE_NAMES.ASSIGN,
            meta: {
              authorizeNavigation({ user }) {
                if (!user?.roles.admin && !user?.roles.transportLeader) {
                  return new Error(ROUTER_ERROR_MESSAGE_403)
                }
                return undefined
              },
              authRequired: true,
              title: () => t('routes.assign'),
            },
            component: () => import('@/views/assign/ViewAssign.vue'),
          },
          {
            path: 'deviations',
            name: ROUTE_NAMES.DEVIATIONS,
            meta: {
              authorizeNavigation({ user }) {
                if (!user?.roles.admin && !user?.roles.transportLeader) {
                  return new Error(ROUTER_ERROR_MESSAGE_403)
                }
                return undefined
              },
              authRequired: true,
              title: () => t('routes.deviations'),
            },
            component: () => import('@/views/deviations/ViewDeviations.vue'),
          },
          {
            path: 'vehicle/:resource',
            component: () => import('@/views/current-resource-provider/ViewCurrentResourceProvider.vue'),
            meta: {
              async authorizeNavigation({ runBy, user, previousUser, toRoute }) {
                const hasElevatedAccess = !!user?.roles.admin || !!user?.roles.transportLeader
                const nextDriverConfig = user?.driverConfig
                const prevDriverConfig = previousUser?.driverConfig

                if (runBy === 'navigation-guard') {
                  const userRegionCode = user?.driverConfig?.region
                  const userResourceCode = user?.driverConfig?.resource
                  const isMissingRequiredConfig = !userRegionCode || !userResourceCode
                  if (isMissingRequiredConfig && !hasElevatedAccess) {
                    return new Error(ROUTER_ERROR_MESSAGE_MISSES_DRIVER_CONFIG)
                  }

                  const regionCode = stringToRegionCode(unwrapArray(toRoute.params.region))
                  const resourceCode = unwrapArray(toRoute.params.resource)
                  const resources = await new ResourceRepository().getAllActiveByRegion(regionCode)
                  const resourceExists = resources.some(r => r.resourceCode === resourceCode)
                  if (!resourceExists) {
                    return new Error(ROUTER_ERROR_MESSAGE_404)
                  }
                  const resourceBelongsToUser = userResourceCode === resourceCode && userRegionCode === regionCode
                  const isAuthorized = hasElevatedAccess || resourceBelongsToUser
                  if (!isAuthorized) {
                    return new Error(ROUTER_ERROR_MESSAGE_403)
                  }
                }

                if (runBy === 'watcher') {
                  if (hasElevatedAccess) return
                  if (!prevDriverConfig) return
                  if (
                    nextDriverConfig?.resource === prevDriverConfig.resource &&
                    nextDriverConfig.region === prevDriverConfig.region
                  ) {
                    return
                  }
                  // User does not have access to the current resource anymore.
                  // Either they have a new resource, or they are missing a driver config.
                  // Send them to the home page, which will redirect them to the correct new page.
                  return {
                    name: ROUTE_NAMES.HOME,
                  }
                }
                return undefined
              },
              authRequired: true,
            },
            redirect: { name: ROUTE_NAMES.ORDER_LIST },
            children: [
              {
                path: 'order/:id',
                name: ROUTE_NAMES.ORDER,
                meta: {
                  authRequired: true,
                  title: () => t('routes.order'),
                  async authorizeNavigation({ user, toRoute }) {
                    const hasElevatedAccess = !!user?.roles.admin || !!user?.roles.transportLeader
                    const driverConfig = user?.driverConfig

                    if (hasElevatedAccess) return

                    const entryId = unwrapArray(toRoute.params.id)
                    const entry = await entryRepository.get(entryId)

                    const isEntryAssignedToCurrentVehicle =
                      entry.order.resource?.resourceCode === driverConfig?.resource

                    if (isEntryAssignedToCurrentVehicle) return

                    return {
                      name: ROUTE_NAMES.HOME,
                    }
                  },
                  beforeResolve(to) {
                    return redirectIfOrderMissing(to, todayIso.value, {
                      name: ROUTE_NAMES.ORDER_LIST,
                      params: {
                        region: to.params.region,
                        resource: to.params.resource,
                      },
                    })
                  },
                },
                component: () => import('@/views/order/ViewOrder.vue'),
              },
              {
                path: 'parked',
                name: ROUTE_NAMES.PARKED_LIST,
                meta: { authRequired: true, title: () => t('routes.parked') },
                component: () => import('@/views/parked-list/ViewParkedList.vue'),
              },
              {
                path: 'parked/:docId',
                name: ROUTE_NAMES.PARKED_CONTAINER,
                meta: {
                  authRequired: true,
                  title: () => t('routes.parked-container'),
                  async authorizeNavigation({ toRoute }) {
                    const paredRepo = new ParkedRepository()
                    const docId = unwrapArray(toRoute.params.docId)
                    try {
                      await paredRepo.get(docId)
                    } catch {
                      return new Error(ROUTER_ERROR_MESSAGE_404)
                    }
                    return undefined
                  },
                },
                component: () => import('@/views/parked-details/ViewParkedContainer.vue'),
              },

              {
                // We don't seem to redirect here from within the app,
                // but we do get traffic on it.
                path: 'overview',
                redirect: { name: ROUTE_NAMES.ORDER_LIST },
              },

              {
                path: '',
                component: () => import('@/views/current-date-provider/ViewCurrentDateProvider.vue'),
                children: [
                  {
                    path: 'date/:date',
                    name: ROUTE_NAMES.ORDER_LIST_BY_DATE,
                    meta: {
                      authorizeNavigation({ user }) {
                        // only transport leaders can go to a specific date
                        if (!user?.roles.admin && !user?.roles.transportLeader) {
                          return new Error(ROUTER_ERROR_MESSAGE_403)
                        }
                        return undefined
                      },
                      authRequired: true,
                    },
                    component: () => import('@/views/order-list/ViewOrderList.vue'),
                  },
                  {
                    path: 'date/:date/order/:id',
                    name: ROUTE_NAMES.ORDER_BY_DATE,
                    meta: {
                      authRequired: true,
                      title: () => t('routes.order'),
                      authorizeNavigation({ user }) {
                        // only transport leaders can browse orders on a specific date
                        if (!user?.roles.admin && !user?.roles.transportLeader) {
                          return new Error(ROUTER_ERROR_MESSAGE_403)
                        }
                        return undefined
                      },
                      beforeResolve(to) {
                        const date = unwrapArray(to.params.date)
                        return redirectIfOrderMissing(to, date, {
                          name: ROUTE_NAMES.ORDER_LIST_BY_DATE,
                          params: {
                            region: to.params.region,
                            resource: to.params.resource,
                            date,
                          },
                        })
                      },
                    },
                    component: () => import('@/views/order/ViewOrder.vue'),
                  },
                  {
                    path: '',
                    name: ROUTE_NAMES.ORDER_LIST,
                    meta: {
                      authRequired: true,
                      title: () => t('routes.order-list'),
                    },
                    component: () => import('@/views/order-list/ViewOrderList.vue'),
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        path: '/manage-user-roles',
        name: ROUTE_NAMES.ADMIN,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.roles.admin) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: () => t('admin.title'),
        },
        component: () => import('@/views/admin/ViewAdmin.vue'),
      },
    ],
  },

  // OUTSIDE MAIN LAYOUT

  {
    // Catch all remaining and display 404 view
    // Must come before the remaining unreachable routes,
    // otherwise they would get matched first.
    path: MASKED_ROUTE_PATH,
    meta: { title: () => t('error.status.404') },
    component: () => import('@/views/error/ViewError.vue'),
    props: (): ViewErrorProps => ({
      title: t('error.status.404'),
    }),
  },

  {
    // not reachable by URL, but can be rendered programmatically by name
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_MISSING_DRIVER_CONFIG,
    meta: {
      authorizeNavigation({ user, runBy }) {
        if (runBy === 'watcher') {
          const hasElevatedAccess = !!user?.roles.admin || !!user?.roles.transportLeader
          const driverConfigExists = user?.driverConfig?.region && user?.driverConfig?.resource
          const shouldStillSeeErrorPage = !hasElevatedAccess && !driverConfigExists
          if (!shouldStillSeeErrorPage) {
            return { name: ROUTE_NAMES.HOME }
          }
        }
        return undefined
      },
      authRequired: true,
      title: () => t('driver.config.meta-title'),
    },
    component: () => import('@/views/driver-config/ViewDriverConfig.vue'),
  },

  {
    // not reachable by URL, but can be rendered programmatically by name
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_403,
    meta: { title: () => t('error.status.403') },
    component: () => import('@/views/error/ViewError.vue'),
    props: (): ViewErrorProps => {
      const userEmail = useUser().user.value?.email
      return {
        title: t('error.status.403'),
        subtitle: !userEmail ? null : `${t('driver.config.logged-in-as')} ${userEmail}`,
      }
    },
  },

  {
    // not reachable by URL, but can be rendered programmatically by name
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_404,
    meta: { title: () => t('error.status.404') },
    component: () => import('@/views/error/ViewError.vue'),
    props: (): ViewErrorProps => ({
      title: t('error.status.404'),
    }),
  },

  {
    // not reachable by URL, but can be rendered programmatically by name.
    // Props must be provided when navigating to this route.
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_GENERIC,
    meta: { title: () => t('error.error') },
    component: () => import('@/views/error/ViewError.vue'),
    // Allows props to be passed to the error page via query params
    // e.g. '/?error=that+went+wrong'
    props: (route): ViewErrorProps => ({ subtitle: unwrapArray(route.query.error) }),
  },
]

/**
 * If we refresh the page while viewing an order then we have to verify that the order
 * still exists on the vehicle, on the date it was being viewed on.
 */
async function redirectIfOrderMissing(
  to: RouteLocationNormalized,
  date: string,
  fallback: RouteLocationRaw
): Promise<RouteLocationRaw | undefined> {
  const entries = await new EntryRepository().getAllForOrderList(
    `${date}T00:00:00.0000000`,
    unwrapArray(to.params.resource),
    regionToCompanies(stringToRegionCode(unwrapArray(to.params.region)))
  )

  const entry = entries.find(e => {
    const oId = e.order.subOrderCode ?? String(e.order.id)
    return oId === String(to.params.id)
  })

  return entry ? undefined : fallback
}
